import { useCallback, useEffect, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { loginSchema, shownFieldError } from "../../utils/validationPatterns";
import useAuth from "../../hooks/useAuth";
import Plans, { decideGeneralName, PlanNames } from "../../components/Plans";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import useAppLocalStorage from "../../hooks/useAppLocalStorage";
import { useTranslation } from "react-i18next";
import { AlertMessage } from "../../components/atoms/AlertMessage";
import { ModalShell } from "../../components/atoms/ModalShell";
import { HiEye, HiEyeOff, HiOfficeBuilding } from "react-icons/hi";
import SocialAuth from "../../components/pages/lmcUserVIew/SocialAuth";
import { IResolveParams } from "reactjs-social-login";
import { queryClient } from "../../providers/QueryProvider";
import { CustomButton } from "../../components/atoms/CustomButton";
import {
  useOwnderLogin,
  useOwnderLoginSocial,
  useOwnderSendValidationLink,
} from "../../hooks/entity";
import { useGetVerifyPlansMutation } from "../../hooks/billing";
import SecureStorage from "../../utils/secureStorage";
import { buildForwardedSearch } from "../../utils/attributionCapture";
import { shouldShowUpgradePrompts } from "../../utils/rolePaymentConfig";
import { Timer } from "../../utils/Timer";
import { PassLessLoginModal } from "../../components/pages/auth/PassLessLoginModal";
import { PasskeyLoginButton } from "../../components/pages/auth/PasskeyLoginButton";
import {
  notifyPasskeyError,
  startPasskeyAutofill,
} from "../../hooks/auth/passwordless";

const Login = () => {
  const localStorage = useAppLocalStorage();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const auth = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger,
  } = useForm({
    resolver: joiResolver(loginSchema),
  });

  const [step, setStep] = useState<"identify" | "authenticate">("identify");
  const [showPassLessModal, setShowPassLessModal] = useState(false);
  const [entityIdValue, setEntityIdValue] = useState<string>("");
  const entityIdRef = useRef<string>("");

  const from =
    (location.state as { from?: { pathname?: string } })?.from?.pathname ||
    "/control-panel";

  const [showTimer, setShowTimer] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);

  const [userId, setUserId] = useState<string>();
  const [userEntityId, setUserEntityId] = useState<string>();
  // Carried to the plan modal because it is rendered BEFORE auth.login() has
  // stored the session, so the shared interceptor has nothing to attach. The
  // free-subscription endpoint requires authentication (finding R1).
  const [userAccessToken, setUserAccessToken] = useState<string>();
  const [user_email, setUser_email] = useState("");

  const [isEmailVerificationError, setIsEmailVerificationError] =
    useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [enableEntityId, setEnableEntityId] = useState<boolean>(false);

  useEffect(() => {
    const fromUrl = (
      searchParams.get("entity_identification") ||
      searchParams.get("entity_id") ||
      ""
    ).trim();
    const remembered = (SecureStorage.getTeamMemberEntityId() || "").trim();
    const prefill = fromUrl || remembered;
    if (prefill) {
      setEntityIdValue(prefill);
      entityIdRef.current = prefill;
      setEnableEntityId(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { mutate: ownerLogin, isLoading: isLogin } = useOwnderLogin();

  const { mutate: ownerLoginSocial, isLoading: isSocialLogin } =
    useOwnderLoginSocial();

  const { mutate: resendValitionLink } = useOwnderSendValidationLink();

  const { mutateAsync: getVerifiedPlans } = useGetVerifyPlansMutation();

  const autoLoginSocialUser = useCallback(
    (parsedCredential: any, user_selected_plan: string) => {
      ownerLoginSocial(
        { data: { type: "login", providerPayload: parsedCredential } },
        {
          onSuccess: ({ data }) => {
            setUser_email(data.element.user_email);
            processLogin(
              data.element,
              user_selected_plan,
              rememberMe,
              "_umd_oauth_credential",
              {
                _umd_oauth_credential: JSON.stringify(parsedCredential),
              }
            );
          },
        }
      );
    },
    [auth, navigate, rememberMe]
  );

  useEffect(() => {
    let cleaner = true;

    if (cleaner && auth.user.isAuthenticated)
      return navigate(from + window.location.search);

    SecureStorage.removeInsecureStorage();

    let user_selected_plan = SecureStorage.getSelectedPlan();
    let oauthCredentials = SecureStorage.getOAuthCredentials();

    if (
      cleaner &&
      oauthCredentials &&
      user_selected_plan &&
      searchParams.has("status") &&
      searchParams.get("status") === "true"
    ) {
      return autoLoginSocialUser(oauthCredentials, user_selected_plan);
    }

    if (
      cleaner &&
      oauthCredentials &&
      user_selected_plan &&
      searchParams.has("status") &&
      searchParams.get("status") === "false"
    ) {
      SecureStorage.clearOAuthCredentials();
      SecureStorage.clearSelectedPlan();
      return;
    }

    if (
      cleaner &&
      user_selected_plan &&
      searchParams.has("status") &&
      searchParams.get("status") === "true"
    ) {
      const userPrefs = SecureStorage.getUserPreferences();

      if (userPrefs?.user_email) {
        setValue("user_email", userPrefs.user_email);
        setUser_email(userPrefs.user_email);
      }
    }

    return () => {
      cleaner = false;
    };
  }, [
    auth,
    from,
    navigate,
    reset,
    autoLoginSocialUser,
    searchParams,
    localStorage,
    setValue,
  ]);

  const handleResendValidationEmail = () => {
    if (userId) {
      resendValitionLink(
        {
          user_identification: userId,
        },
        {
          onSuccess: () => {
            setShowTimer(true);
          },
          onError: (err) => {
            setShowTimer(false);
          },
        }
      );
    }
  };

  const handleLoginStartSocialAuth = () => {};

  const handleResolveSocialAuth = (
    provider: IResolveParams["provider"],
    data: IResolveParams["data"]
  ) => {
    try {
      const cpPayload: Record<string, string | undefined> = {
        access_token:
          provider === "google"
            ? data?.access_token
            : data?.authorization?.id_token,
        provider,
      };

      if (entityIdRef.current?.trim()) {
        cpPayload.entity_id = entityIdRef.current.trim();
      }

      ownerLoginSocial(
        { data: { type: "login", providerPayload: cpPayload } },
        {
          onSuccess: ({ data }) => {
            setUser_email(data.element.user_email);
            processLogin(
              data.element,
              null,
              rememberMe,
              "_umd_oauth_credential",
              {
                _umd_oauth_credential: JSON.stringify(cpPayload),
              }
            );
          },
        }
      );
    } catch (ex) {
      console.log(ex);
    }
  };

  const handleRejectSocialAuth = (err: any) => {
    console.log(err);
  };

  const authenticatedRef = useRef<
    ((userData: any, userEmail: string) => void) | null
  >(null);

  useEffect(() => {
    let abandoned = false;

    startPasskeyAutofill()
      .then((response) => {
        if (abandoned) {
          return;
        }
        const userData = response?.data?.element;
        if (!userData) {
          return;
        }
        if (!userData.access_token) {
          toast.error(t("passkey.error") as string);
          return;
        }
        authenticatedRef.current?.(userData, userData.user_email || "");
      })
      .catch((error) => {
        if (!abandoned) {
          notifyPasskeyError(error, t, toast);
        }
      });

    return () => {
      abandoned = true;
    };
    // Mount only: re-running this would restart the WebAuthn conditional ceremony on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onIdentify = async (event: React.FormEvent) => {
    event.preventDefault();
    if (await trigger("user_email")) {
      setStep("authenticate");
    }
  };

  const onSubmit = (data: any) => {
    const entityId = entityIdRef.current?.trim();
    const input = {
      ...data,
      ...(entityId ? { entity_id: entityId } : {}),
    };
    ownerLogin(
      {
        credentials: input,
      },
      {
        onError: (err) => {
          if (
            err.response?.status === 400 &&
            err.response?.data.code === "RSUSKO014"
          ) {
            setUserId(err.response?.data.element.user_identification);
            setIsEmailVerificationError(true);
            setShowAlert(true);
          }
        },
        onSuccess: async (res) => {
          if (!res?.data?.element) {
            console.error(
              "Login response missing expected data structure:",
              res
            );
            return;
          }

          const userData = res.data.element;

          setUser_email(data.user_email);

          SecureStorage.setUserPreferences({
            user_email: data.user_email,
            rememberMe,
            is_new_user: userData.entity_status === "NEW",
          });

          const { shouldShowPlansModal } = await processLogin(
            userData,
            SecureStorage.getSelectedPlan(),
            rememberMe
          );

          if (!shouldShowPlansModal) {
            if (
              searchParams.has("status") &&
              searchParams.get("status") === "true"
            ) {
              const plan = SecureStorage.getSelectedPlan();
              SecureStorage.clearSelectedPlan();
              navigate(
                `/control-panel?plan=${decideGeneralName(
                  plan as PlanNames
                )}&plan_name=${plan}&status=true`,
                { replace: true }
              );
            } else {
              navigate(from, { replace: true });
            }
          }
        },
      }
    );
  };

  /**
   * Completion shared by the passwordless and passkey flows. Both end on the same access token the
   * password login returns, so everything after this point is the existing session handling.
   */
  const onPasswordlessAuthenticated = async (
    userData: any,
    userEmail: string
  ) => {
    setShowPassLessModal(false);
    setUser_email(userEmail);

    SecureStorage.setUserPreferences({
      user_email: userEmail,
      rememberMe,
      is_new_user: userData.entity_status === "NEW",
    });

    const { shouldShowPlansModal } = await processLogin(
      userData,
      SecureStorage.getSelectedPlan(),
      rememberMe
    );

    if (!shouldShowPlansModal) {
      navigate(from, { replace: true });
    }
  };

  // The autofill promise stays pending until the user picks a passkey, long enough for them to
  // reach step two and tick "remember me". Reading the callback through a ref keeps that choice,
  // where the mount-time closure would have kept the value it had when the page loaded.
  authenticatedRef.current = onPasswordlessAuthenticated;

  const processLogin = async (
    userData: any,
    user_selected_plan: string | null,
    rememberMe: boolean,
    _storeCredentialKey?: string,
    additionalStorage?: Record<string, string>
  ): Promise<{ shouldShowPlansModal: boolean }> => {
    if (!userData?.user_identification || !userData?.entity_identification) {
      console.error(
        "[processLogin] login response missing required identifiers",
        {
          hasUserId: !!userData?.user_identification,
          hasEntityId: !!userData?.entity_identification,
        }
      );
      toast.error("Login response was incomplete. Please login again.");
      return { shouldShowPlansModal: false };
    }

    const usedEntityId = entityIdRef.current?.trim();
    if (usedEntityId) {
      try {
        SecureStorage.setTeamMemberEntityId(usedEntityId);
      } catch {}
    }
    return new Promise<{ shouldShowPlansModal: boolean }>((resolve, reject) => {
      getVerifiedPlans(
        {
          user_identification: userData.user_identification,
          entity_identification: userData.entity_identification,
          access_token: userData.access_token,
        },
        {
          onSuccess: async (value: any) => {
            const result = handleVerifyPlansSuccess({
              value,
              userData,
              user_selected_plan,
              rememberMe,
            });

            resolve({ shouldShowPlansModal: result.shouldShowPlansModal });
          },
          onError: async (err: any) => {
            handleVerifyPlansError({
              err,
              userData,
            });

            reject(err);
          },
        }
      );
    }).finally(() => {
      if (additionalStorage) {
        Object.entries(additionalStorage).forEach(([k, v]) =>
          localStorage.setItem(k, v)
        );
      }
    });
  };

  const handleVerifyPlansSuccess = ({
    value,
    userData,
    user_selected_plan,
    rememberMe,
    navigateToControlPanel = true,
  }: {
    value: any;
    userData: any;
    user_selected_plan: string | null;
    rememberMe: boolean;
    navigateToControlPanel?: boolean;
  }): { shouldShowPlansModal: boolean } => {
    const showUpgrades = shouldShowUpgradePrompts(userData.user_role);

    const borrowsOwnerPlan = value.data.element.borrows_owner_plan === true;

    const shouldShowPlansModal =
      showUpgrades &&
      !borrowsOwnerPlan &&
      (value?.data?.element?.is_new_user ||
        !value?.data?.element?.customer_plans?.length);

    if (borrowsOwnerPlan) {
      const hasOwnerSubscription =
        (value.data.element.owner_plans || []).length > 0;
      auth.onOffUserEndedSubscription(!hasOwnerSubscription);
    } else if (!value?.data?.element?.customer_plans?.length) {
      auth.onOffUserEndedSubscription(true);
    } else {
      auth.onOffUserEndedSubscription(false);
    }

    const loginData = {
      ...userData,
      trial_left_days: value.data.element.trial_left_days,
      is_promo_trial: value.data.element.is_promo_trial || false,
      trial_restrictions: value.data.element.trial_restrictions,
      trial_end_date: value.data.element.trial_end_date,
      customer_plans: value.data.element.customer_plans || [],
      customer_top_plan: value.data.element.customer_top_plan || "",
      is_new_user: value.data.element.is_new_user || false,
      is_root: userData.is_root || false,
      lastPaymentCheck: Date.now(),
      hasValidSubscription: borrowsOwnerPlan
        ? (value.data.element.owner_plans || []).length > 0
        : (value.data.element.customer_plans || []).length > 0,
      rememberMe,
      password_validation_regex_level:
        value.data.element.password_validation_regex_level || "level2",
      user_role:
        userData.user_role || value.data.element.user_role || "DEFAULT",
      user_type:
        userData.user_type || value.data.element.user_type || "DEFAULT",
      borrows_owner_plan: borrowsOwnerPlan,
      owner_plans: value.data.element.owner_plans || [],
      owner_top_plan: value.data.element.owner_top_plan || "",
    };

    auth.login(loginData);

    if (shouldShowPlansModal) {
      const emailFromForm = watch("user_email");
      const userPrefs = SecureStorage.getUserPreferences();
      const emailFromStorage = userPrefs?.user_email;

      const availableEmail = emailFromForm || emailFromStorage;

      if (availableEmail) {
        setUser_email(availableEmail);
      }

      setUserId(userData.user_identification);
      setUserEntityId(userData.entity_identification);
      setUserAccessToken(userData.access_token);
      setShowPlansModal(true);
      return { shouldShowPlansModal: true };
    }

    const isPaymentReturn = searchParams.get("status") === "true";
    if (navigateToControlPanel && user_selected_plan && isPaymentReturn) {
      const planParam = `?plan=${decideGeneralName(
        user_selected_plan as PlanNames
      )}&plan_name=${user_selected_plan}&status=true`;
      navigate(`/control-panel${planParam}`, { replace: true });
    }

    return { shouldShowPlansModal: false };
  };

  const handleVerifyPlansError = ({
    err,
    userData,
  }: {
    err: any;
    userData: any;
  }) => {
    if (
      err.response?.data &&
      err.response?.data?.message !== "Payment checking"
    )
      toast.error(err.response?.data?.message);
    else if (!err.response) toast.error(err.message);

    if (err.response?.status === 402) {
      setShowPlansModal(true);
      setUserId(userData.user_identification);
      setUserEntityId(userData.entity_identification);
      setUserAccessToken(userData.access_token);
    }
  };

  return (
    <div className="login-page">
      <Helmet>
        <title>Sign in | Super User Manager</title>
        <meta
          name="description"
          content="Super User Manager - Login to dashboard"
        />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/login`}
        />
      </Helmet>

      <div className="w-full md:w-[448px] flex flex-col items-start">
        <div className="w-full flex flex-col items-center gap-6 mb-8">
          <div className="flex flex-col items-center gap-2">
            <h1 className="text-center text-gray-700 text-2xl font-bold font-figtree leading-9">
              {t("login.title")}
            </h1>
            <div className="flex flex-wrap justify-center items-center gap-1 text-center text-gray-700 text-sm font-normal font-figtree leading-tight">
              <span>{t("login.registerNote")}</span>
              <Link
                to={`/register${buildForwardedSearch(location.search)}`}
                className="text-blue-600 text-sm font-medium font-figtree leading-tight hover:underline"
              >
                {t("login.registerBtn")}
              </Link>
            </div>
          </div>
        </div>

        {step === "identify" && (
          <SocialAuth
            className="w-full"
            providers={["Apple", "Google"]}
            onLoginStartSocialAuthStart={handleLoginStartSocialAuth}
            onResolveSocialAuth={handleResolveSocialAuth}
            onRejectSocialAuth={handleRejectSocialAuth}
            providersIcon={{
              Apple: (
                <div className="relative w-full h-11 mb-4 px-2.5 py-2 bg-white hover:bg-blue-400/5 rounded-md shadow border border-gray-300 flex justify-center items-center cursor-pointer">
                  <img
                    src="../../images/apple-black.svg"
                    alt=""
                    className="absolute left-2.5 top-2.5 w-6 h-6"
                  />
                  <p className="text-gray-700 text-sm font-medium font-inter leading-tight">
                    {t("login.socialApple")}
                  </p>
                </div>
              ),
              Google: (
                <div className="relative w-full h-11 px-2.5 py-2 bg-white hover:bg-blue-400/5 rounded-md shadow border border-gray-300 flex justify-center items-center cursor-pointer">
                  <img
                    src="../../images/google.svg"
                    alt=""
                    className="absolute left-2.5 top-2.5 w-6 h-6"
                  />
                  <p className="text-gray-700 text-sm font-medium font-inter leading-tight">
                    {t("login.socialGoogle")}
                  </p>
                </div>
              ),
            }}
            redirectUrl={{
              Apple: (process.env.REACT_APP_HOST_URL as string) + "/login",
              Google: (process.env.REACT_APP_HOST_URL as string) + "/login",
            }}
          />
        )}

        {step === "identify" && (
          <div className="w-full my-3 text-center text-gray-500 text-sm font-extrabold font-figtree leading-tight">
            {t("login.or")}
          </div>
        )}

        {searchParams.has("payment-status") &&
          searchParams.get("payment-status") === "failure" && (
            <AlertMessage color="failure" className="w-full">
              <span className="font-medium">{t("login.alert_1")}</span>
            </AlertMessage>
          )}

        {searchParams.has("status") &&
          searchParams.get("status") === "true" && (
            <AlertMessage color="success" className="w-full mb-3">
              <span className="font-medium">{t("login.alert_2")}</span>
            </AlertMessage>
          )}

        {isEmailVerificationError && showAlert && (
          <AlertMessage
            color="warning"
            className="w-full mb-3"
            onDismiss={() => setShowAlert(false)}
          >
            {t("login.alert_3")}
            {showTimer ? (
              <span className="text-muted ml-2">
                <Timer setShowTimer={setShowTimer} ttl={120} />{" "}
                {t("login.alert_4")}
              </span>
            ) : (
              <CustomButton
                variant="none"
                className="ml-2 underline"
                style={{ verticalAlign: "top", outline: "none" }}
                onClick={handleResendValidationEmail}
                disabled={showTimer}
                title={t("login.btnResend") as string}
              />
            )}
          </AlertMessage>
        )}

        <form
          onSubmit={step === "identify" ? onIdentify : handleSubmit(onSubmit)}
          className="w-full flex flex-col gap-6"
        >
          <div
            className={`w-full bg-white rounded-md border ${
              Object.keys(errors).length > 0
                ? "border-red-500"
                : "border-gray-300"
            } flex flex-col`}
          >
            <div
              className={`w-full px-3.5 py-2 bg-white rounded-t-md shadow flex items-center ${
                step === "identify" ? "rounded-b-md" : ""
              }`}
            >
              <input
                type="email"
                placeholder={t("login.label_1").toString()}
                className="w-full p-0 text-base text-gray-700 font-normal font-figtree leading-normal border-none focus:ring-0"
                readOnly={step === "authenticate"}
                autoComplete="username webauthn"
                {...register("user_email")}
              />
              {step === "authenticate" && (
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-blue-600 hover:underline"
                  onClick={() => setStep("identify")}
                >
                  {t("login.changeEmail")}
                </button>
              )}
            </div>
            {step === "authenticate" && (
              <div className="w-full px-3.5 py-2 bg-white rounded-b-md shadow border-t border-gray-300 flex items-center justify-between">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full p-0 text-base text-gray-700 font-normal font-figtree leading-normal border-none focus:ring-0"
                  placeholder={t("login.label_2").toString()}
                  autoFocus
                  {...register("user_password")}
                />

                <button
                  className="w-5 h-5 flex-shrink-0"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {!showPassword ? (
                    <HiEye className="text-gray-400" size={20} />
                  ) : (
                    <HiEyeOff className="text-gray-400" size={20} />
                  )}
                </button>
              </div>
            )}
          </div>

          {step === "authenticate" && entityIdValue.trim() && (
            <p className="-mt-4 text-xs text-gray-500">
              {t("login.teamMemberPlaceholder")}{" "}
              <span className="font-medium text-gray-700">
                {entityIdValue.trim()}
              </span>
            </p>
          )}

          {step === "authenticate" && (
            <div className="w-full flex flex-col gap-2">
              <PasskeyLoginButton
                email={watch("user_email") || ""}
                entityIdentification={entityIdValue}
                onAuthenticated={onPasswordlessAuthenticated}
              />
              <CustomButton
                variant="secondary"
                size="md"
                className="w-full"
                title={t("login.passwordlessBtn") as string}
                onClick={() => setShowPassLessModal(true)}
              />
            </div>
          )}

          {step === "authenticate" && (
            <div className="w-full flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 bg-white rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  id="remember-me"
                />
                <label
                  className="text-gray-700 text-sm font-normal font-figtree leading-tight"
                  htmlFor="remember-me"
                >
                  {t("login.label_3")}
                </label>
              </div>
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-blue-600 text-sm font-medium font-figtree leading-tight hover:underline"
                >
                  {t("login.forgotPassWordBtn")}
                </Link>
              </div>
            </div>
          )}

          {Object.keys(errors).length > 0 && (
            <div>
              {Object.keys(errors).map((key) => (
                <p key={key} className="mb-0 text-xs text-red-600">
                  {shownFieldError(t, errors[key]?.message?.toString())}
                </p>
              ))}
            </div>
          )}

          {step === "identify" ? (
            <CustomButton
              variant="primary"
              type="button"
              className="w-full"
              title={t("login.continueBtn") as string}
              onClick={onIdentify}
            />
          ) : (
            <CustomButton
              variant="primary"
              type="submit"
              className="w-full"
              disabled={
                isLogin ||
                isSocialLogin ||
                queryClient.getQueryState(["payment-status"])?.isFetching
              }
              isLoading={
                isLogin ||
                isSocialLogin ||
                queryClient.getQueryState(["payment-status"])?.isFetching
              }
              title={t("login.loginBtn") as string}
            />
          )}
        </form>

        <div
          className={`w-full mt-4 text-center ${
            step === "identify" ? "" : "hidden"
          }`}
        >
          <button
            type="button"
            onClick={() => setEnableEntityId(!enableEntityId)}
            className="text-xs text-gray-400 hover:text-gray-600 font-normal font-figtree transition-colors duration-200"
          >
            {t("login.teamMemberToggle")}
          </button>

          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              enableEntityId ? "max-h-40 opacity-100 mt-3" : "max-h-0 opacity-0"
            }`}
          >
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-left">
              <div className="flex items-center gap-3 px-3 py-2.5 bg-white rounded-md border border-gray-300">
                <HiOfficeBuilding className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder={t("login.teamMemberPlaceholder") as string}
                  className="w-full p-0 text-base md:text-sm text-gray-700 font-normal font-figtree leading-normal bg-transparent border-none focus:ring-0"
                  value={entityIdValue}
                  onChange={(e) => {
                    setEntityIdValue(e.target.value);
                    entityIdRef.current = e.target.value;
                  }}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500 font-figtree">
                {t("login.teamMemberHelp")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <PassLessLoginModal
        show={showPassLessModal}
        onClose={() => setShowPassLessModal(false)}
        defaultEmail={watch("user_email") || ""}
        entityIdentification={entityIdValue}
        onAuthenticated={onPasswordlessAuthenticated}
      />

      <ModalShell
        show={showPlansModal}
        onClose={() => {}}
        size="7xl"
        title={t("login.text_1")}
        showCloseButton={false}
      >
        <Plans
          user_identification={userId as string}
          entity_identification={userEntityId as string}
          access_token={userAccessToken}
          user_email={user_email}
          isInBilling={false}
          onCloseModal={() => {
            setShowPlansModal(false);
          }}
        />
      </ModalShell>
    </div>
  );
};

export default Login;
